import type { LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';

/**
 * Sentinel for "filter is unset". Stored in the toolbar's value object
 * but stripped from the URL — unset filters never appear in the query string.
 */
export const UNSET = '__all' as const;

export type FilterChipType = 'select' | 'combobox' | 'date-range' | 'address' | 'number-range';

export interface FilterChipOption {
  value: string;
  label: string;
  /** Optional swatch color shown next to the option label (select chips). */
  color?: string;
  /** Optional row count badge shown on the right of the option (select/combobox chips). */
  count?: number;
  disabled?: boolean;
}

export interface FilterChipDateRangePreset {
  /** Preset key stored in URL (e.g. `30d`, `mtd`, `ytd`). */
  value: string;
  label: string;
}

interface FilterChipDefBase {
  /** URL key + value-shape key. Must be unique across the toolbar. */
  name: string;
  /** Shown as the chip's label when the chip is unset (e.g. "Status"). */
  label?: string;
  icon?: LucideIcon;
  /**
   * Initial / cleared value. For most chips this is `__all` (UNSET).
   * For date-range chips this is typically a preset key (e.g. `30d`).
   */
  defaultValue?: string;
  disabled?: boolean;
  /** Optional formatter for the chip's active value text. */
  renderValue?: (value: string | string[]) => ReactNode;
}

export interface FilterChipSelectDef extends FilterChipDefBase {
  type: 'select';
  options: FilterChipOption[];
  multi?: false;
}

export interface FilterChipComboboxDef extends FilterChipDefBase {
  type: 'combobox';
  options: FilterChipOption[];
  /** When true, the chip stores `string[]` and the popover uses checkbox rows. */
  multi?: boolean;
  /** Label for the "Select all" row when `multi` is true. */
  selectAllLabel?: string;
  /**
   * Single-select only. When true, the user can submit a value not present in
   * `options` — the search field surfaces a "Use '<typed>'" item and Enter
   * commits the typed value as the chip's value. Use for hybrid pick-or-type
   * filters (e.g. filter by an active item OR by a historical / deleted name).
   */
  freeSolo?: boolean;
  /**
   * Optional caption rendered above `options` in the dropdown (e.g. "Active").
   * Pair with `freeSolo` to label the predefined options as a category, separate
   * from the free-text affordance.
   */
  groupLabel?: string;
}

export interface FilterChipDateRangeDef extends FilterChipDefBase {
  type: 'date-range';
  presets?: FilterChipDateRangePreset[];
  /** Default preset key. Defaults to `30d`. */
  defaultValue?: string;
  /** Enables time-of-day pickers in the popover. */
  enableTime?: boolean;
  /** Override the URL key for the ISO `from` date. Defaults to `${name}From`. */
  fromKey?: string;
  /** Override the URL key for the ISO `to` date. Defaults to `${name}To`. */
  toKey?: string;
}

export interface FilterChipAddressDef extends FilterChipDefBase {
  type: 'address';
  placeholder?: string;
  /**
   * Optional validator. When provided, non-empty values are only committed
   * to the URL if `validate(value)` returns `true`. Empty values are always
   * committed so the user can always clear the chip.
   */
  validate?: (value: string) => boolean;
  /** Debounce in ms before pushing the input value to the URL. Defaults to 600. */
  debounceMs?: number;
}

export interface FilterChipNumberRangeDef extends FilterChipDefBase {
  type: 'number-range';
  /** Optional currency selector options shown next to the min/max inputs. */
  currencyOptions?: FilterChipOption[];
}

export type FilterChipDef =
  | FilterChipSelectDef
  | FilterChipComboboxDef
  | FilterChipDateRangeDef
  | FilterChipAddressDef
  | FilterChipNumberRangeDef;

export interface FilterToolbarSearchDef {
  /** URL key the search debounces to (e.g. `query`). */
  name: string;
  placeholder?: string;
  /** Debounce in ms before pushing input to URL. Defaults to 600. */
  debounceMs?: number;
}

export interface FilterToolbarSchema {
  /** Optional left-aligned full-width search input. Omit to hide. */
  search?: FilterToolbarSearchDef;
  /** Filter chips rendered left-to-right in the order provided. */
  chips: FilterChipDef[];
  /** Optional date-range chip pinned to the right of the toolbar. */
  date?: FilterChipDateRangeDef;
  /**
   * URL key holding the current page (e.g. `page`). When set, changing any filter clears it so
   * results start from the first page instead of stranding the user on a page that no longer
   * exists. Opt-in — pages that omit it keep their current behaviour.
   */
  pageKey?: string;
}

/**
 * Toolbar state shape — flat key/value mirrored to the URL.
 * Single-value chips store `string`; multi-combobox chips store `string[]`.
 * The search field stores `string`. Date-range stores the preset key as `string`,
 * with optional `dateFrom`/`dateTo` ISO strings when the preset is `custom`.
 */
export type FilterToolbarValue = Record<string, string | string[]>;
